/**
 * ODAX (Outcome-Driven Ads Experiences) compatibility matrix.
 *
 * Source: Meta Marketing API v18 docs
 *   https://developers.facebook.com/docs/marketing-api/reference/ad-campaign-group
 *   (the "Restrictions" / ODAX mapping table — pasted into the codebase
 *    so we don't drift from the canonical version each time Meta updates).
 *
 * The matrix is consulted by:
 *   - per-objective ad-set forms to filter the optimization-goal dropdown
 *     by the chosen conversion location
 *   - the backend error rewriter to suggest the right goal when Meta
 *     rejects an incompatible (objective, location, goal) combo
 *
 * Meta uses `destination_type` on the ad set to encode the conversion
 * location for some objectives (Traffic, Engagement-Conversations, Sales,
 * Leads). Other locations are *implied* by the goal itself:
 *   POST_ENGAGEMENT → "On Post"
 *   PAGE_LIKES      → "On Page"
 *   THRUPLAY / TWO_SECOND_CONTINUOUS_VIDEO_VIEWS → "On Video"
 *   EVENT_RESPONSES → "On Event"
 * For those, the form picker is the goal itself; no separate
 * destination_type is sent.
 */

export type OdaxObjective =
  | 'OUTCOME_AWARENESS'
  | 'OUTCOME_TRAFFIC'
  | 'OUTCOME_ENGAGEMENT'
  | 'OUTCOME_LEADS'
  | 'OUTCOME_APP_PROMOTION'
  | 'OUTCOME_SALES';

export type ConversionLocation =
  | 'NONE'           // Awareness, App Promotion — no location picker
  | 'WEBSITE'
  | 'APP'
  | 'MESSENGER'
  | 'WHATSAPP'
  | 'INSTAGRAM'
  | 'INSTANT_FORM'
  | 'CALLS'
  | 'ON_VIDEO'
  | 'ON_POST'
  | 'ON_EVENT'
  | 'ON_PAGE'
  | 'SHOP';

export interface OdaxLocationOption {
  value: ConversionLocation;
  label: string;
  /** Goal codes valid in this (objective, location) cell. */
  goals: string[];
  /**
   * Meta's `destination_type` enum value, when this location maps to one.
   * Some locations (On Post / On Page / On Video / On Event) DON'T set
   * `destination_type` — Meta infers the location from the goal.
   */
  destinationType?: string;
}

// Every cell below was created live (campaign → ad set → creative → ad, all paused) on 2026-09-29/30. Removed
// cells Meta refused outright: Engagement On Post/On Event "Impressions" ("no longer available"), Event responses
// (needs an event creative the CRM can't build), On-site conversions on Website (needs an app id), Engagement
// Website Landing page views / Link clicks / Impressions / Reach and App events under Engagement and Leads
// ("performance goal isn't available"), Quality leads via Messenger.
export const ODAX_MATRIX: Record<OdaxObjective, OdaxLocationOption[]> = {
  OUTCOME_AWARENESS: [
    { value: 'NONE', label: 'Reach the largest number of people', goals: ['AD_RECALL_LIFT', 'REACH', 'IMPRESSIONS'] },
  ],

  OUTCOME_TRAFFIC: [
    { value: 'WEBSITE',   label: 'Website',   goals: ['LANDING_PAGE_VIEWS', 'LINK_CLICKS', 'IMPRESSIONS', 'REACH'], destinationType: 'WEBSITE' },
    { value: 'APP',       label: 'App',       goals: ['LINK_CLICKS', 'REACH'], destinationType: 'APP' },
    { value: 'MESSENGER', label: 'Messenger', goals: ['LINK_CLICKS', 'IMPRESSIONS', 'REACH'], destinationType: 'MESSENGER' },
    { value: 'WHATSAPP',  label: 'WhatsApp',  goals: ['LINK_CLICKS', 'IMPRESSIONS', 'REACH'], destinationType: 'WHATSAPP' },
  ],

  OUTCOME_ENGAGEMENT: [
    { value: 'ON_POST',    label: 'On Post — promote a post',                    goals: ['POST_ENGAGEMENT', 'REACH'] },
    { value: 'ON_PAGE',    label: 'On Page — invite Page likes',                 goals: ['PAGE_LIKES'], destinationType: 'ON_PAGE' },
    { value: 'ON_VIDEO',   label: 'On Video — optimise for video plays',         goals: ['THRUPLAY', 'TWO_SECOND_CONTINUOUS_VIDEO_VIEWS'], destinationType: 'ON_VIDEO' },
    { value: 'MESSENGER',  label: 'Messenger — start chats',                     goals: ['CONVERSATIONS', 'LINK_CLICKS'], destinationType: 'MESSENGER' },
    { value: 'WHATSAPP',   label: 'WhatsApp — start chats',                      goals: ['CONVERSATIONS', 'LINK_CLICKS'], destinationType: 'WHATSAPP' },
    { value: 'INSTAGRAM',  label: 'Instagram — Direct messages',                 goals: ['CONVERSATIONS', 'LINK_CLICKS'], destinationType: 'INSTAGRAM_DIRECT' },
    { value: 'WEBSITE',    label: 'Website — site engagement',                   goals: ['OFFSITE_CONVERSIONS'], destinationType: 'WEBSITE' },
    { value: 'APP',        label: 'App — in-app engagement',                     goals: ['LINK_CLICKS', 'REACH'], destinationType: 'APP' },
  ],

  OUTCOME_LEADS: [
    // QUALITY_LEAD only passes with destination_type ON_AD (live, 2026-09-29) — sent explicitly for both goals.
    { value: 'INSTANT_FORM', label: 'Instant Forms (lead gen)', goals: ['LEAD_GENERATION', 'QUALITY_LEAD'], destinationType: 'ON_AD' },
    { value: 'WEBSITE',      label: 'Website — convert on site', goals: ['OFFSITE_CONVERSIONS', 'LANDING_PAGE_VIEWS', 'LINK_CLICKS', 'IMPRESSIONS', 'REACH'], destinationType: 'WEBSITE' },
    { value: 'MESSENGER',    label: 'Messenger', goals: ['LEAD_GENERATION'], destinationType: 'MESSENGER' },
    // QUALITY_CALL without destination_type is refused ("performance goal isn't available"); PHONE_CALL passes (live, 2026-09-29).
    { value: 'CALLS',        label: 'Calls',     goals: ['QUALITY_CALL'], destinationType: 'PHONE_CALL' },
    { value: 'APP',          label: 'App',       goals: ['LINK_CLICKS', 'REACH'], destinationType: 'APP' },
  ],

  OUTCOME_APP_PROMOTION: [
    { value: 'APP', label: 'Install / engage with your app', goals: ['LINK_CLICKS', 'APP_INSTALLS', 'APP_INSTALLS_AND_OFFSITE_CONVERSIONS', 'VALUE'], destinationType: 'APP' },
  ],

  OUTCOME_SALES: [
    { value: 'WEBSITE',   label: 'Website',          goals: ['OFFSITE_CONVERSIONS', 'VALUE', 'LANDING_PAGE_VIEWS', 'LINK_CLICKS', 'IMPRESSIONS', 'REACH'], destinationType: 'WEBSITE' },
    { value: 'APP',       label: 'App',              goals: ['OFFSITE_CONVERSIONS', 'LINK_CLICKS', 'REACH'], destinationType: 'APP' },
    { value: 'MESSENGER', label: 'Messenger',        goals: ['CONVERSATIONS', 'OFFSITE_CONVERSIONS', 'LINK_CLICKS', 'IMPRESSIONS', 'REACH'], destinationType: 'MESSENGER' },
    { value: 'WHATSAPP',  label: 'WhatsApp',         goals: ['OFFSITE_CONVERSIONS', 'LINK_CLICKS', 'IMPRESSIONS', 'REACH'], destinationType: 'WHATSAPP' },
  ],
};

/** Friendly labels for goals (inverse of API codes). */
export const GOAL_LABEL: Record<string, string> = {
  AD_RECALL_LIFT: 'Ad recall lift',
  REACH: 'Reach',
  IMPRESSIONS: 'Impressions',
  LANDING_PAGE_VIEWS: 'Landing page views',
  LINK_CLICKS: 'Link clicks',
  POST_ENGAGEMENT: 'Post engagement',
  PAGE_LIKES: 'Page likes',
  EVENT_RESPONSES: 'Event responses',
  THRUPLAY: 'ThruPlay (video)',
  TWO_SECOND_CONTINUOUS_VIDEO_VIEWS: '2-second video views',
  CONVERSATIONS: 'Conversations',
  LEAD_GENERATION: 'Leads',
  QUALITY_LEAD: 'Quality leads',
  QUALITY_CALL: 'Calls',
  OFFSITE_CONVERSIONS: 'Conversions',
  ONSITE_CONVERSIONS: 'On-site conversions',
  VALUE: 'Value (ROAS)',
  APP_INSTALLS: 'App installs',
  APP_INSTALLS_AND_OFFSITE_CONVERSIONS: 'App events',
};

/** Lookup helper — flat valid-goal list for an objective (ALL locations). */
export function allGoalsForObjective(obj: OdaxObjective): string[] {
  const seen = new Set<string>();
  for (const loc of ODAX_MATRIX[obj] ?? []) {
    for (const g of loc.goals) seen.add(g);
  }
  return Array.from(seen);
}

/** Lookup helper — valid goals for a specific (objective, location) cell. */
export function goalsForLocation(obj: OdaxObjective, loc: ConversionLocation): string[] {
  return ODAX_MATRIX[obj]?.find((l) => l.value === loc)?.goals ?? [];
}
